import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { NavLink } from "react-router-dom";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import LocalMallRoundedIcon from "@mui/icons-material/LocalMallRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import { fetchPublicCatalogs } from "src/pages/catalog/publicCatalog";
import {
  readLoginBannerCache,
  writeLoginBannerCache,
} from "./loginBannerCache";

const BANNER_IMAGE_COUNT = 8;
const SLANT = 22;

const BANNER_CATALOG_SLOTS = [87, 55, 40, 5, 47, 61, 60, 59];

const EMPTY_BANNER_IMAGES = Array.from(
  { length: BANNER_IMAGE_COUNT },
  () => null,
);

const TOP_CLIPS = [
  `polygon(0 0, 100% 0, calc(100% - ${SLANT}px) 100%, 0 100%)`,
  `polygon(${SLANT}px 0, 100% 0, calc(100% - ${SLANT}px) 100%, 0 100%)`,
  `polygon(${SLANT}px 0, 100% 0, calc(100% - ${SLANT}px) 100%, 0 100%)`,
  `polygon(${SLANT}px 0, 100% 0, 100% 100%, 0 100%)`,
];

const BOTTOM_CLIPS = [
  `polygon(0 0, calc(100% - ${SLANT}px) 0, 100% 100%, 0 100%)`,
  `polygon(0 0, calc(100% - ${SLANT}px) 0, 100% 100%, ${SLANT}px 100%)`,
  `polygon(0 0, calc(100% - ${SLANT}px) 0, 100% 100%, ${SLANT}px 100%)`,
  `polygon(0 0, 100% 0, 100% 100%, ${SLANT}px 100%)`,
];

const COMPACT_TOP_CLIPS = [TOP_CLIPS[0], TOP_CLIPS[3]];
const COMPACT_BOTTOM_CLIPS = [BOTTOM_CLIPS[0], BOTTOM_CLIPS[3]];
const COMPACT_STRIP_WIDTH = 680;

function pickEndcapImages(images) {
  if (images.length <= 2) return images;
  return [images[0], images[images.length - 1]];
}

const featureItems = [
  {
    icon: <LocalMallRoundedIcon sx={{ fontSize: 20 }} />,
    title: "Exclusive",
    subtitle: "Product Access",
  },
  {
    icon: <SchoolRoundedIcon sx={{ fontSize: 20 }} />,
    title: "Expert",
    subtitle: "Trainings",
  },
  {
    icon: <ForumRoundedIcon sx={{ fontSize: 20 }} />,
    title: "Weekly Q&A",
    subtitle: "with Jesse",
  },
];

function buildBannerImagesFromCatalogs(catalogs = []) {
  const byId = catalogs.reduce((acc, catalog) => {
    if (catalog?.id != null) {
      acc[catalog.id] = catalog;
    }
    return acc;
  }, {});

  const spareImages = catalogs
    .map((catalog) => catalog?.images?.[0])
    .filter(Boolean);

  const usedImages = new Set();

  const slotImages = BANNER_CATALOG_SLOTS.map((catalogId) => {
    const image = byId[catalogId]?.images?.[0];
    if (!image || usedImages.has(image)) return null;
    usedImages.add(image);
    return image;
  });

  const resolved = [...slotImages];
  let spareIndex = 0;

  for (let index = 0; index < BANNER_IMAGE_COUNT; index += 1) {
    if (resolved[index]) continue;

    while (
      spareIndex < spareImages.length &&
      usedImages.has(spareImages[spareIndex])
    ) {
      spareIndex += 1;
    }

    if (spareIndex < spareImages.length) {
      resolved[index] = spareImages[spareIndex];
      usedImages.add(spareImages[spareIndex]);
      spareIndex += 1;
    }
  }

  return resolved;
}

function preloadImages(urls) {
  const uniqueUrls = [...new Set(urls.filter(Boolean))];
  if (!uniqueUrls.length) return Promise.resolve();

  return Promise.all(
    uniqueUrls.map(
      (url) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(url);
          img.onerror = () => resolve(url);
          img.src = url;
        }),
    ),
  );
}

function getInitialBannerImages() {
  const cached = readLoginBannerCache();
  if (cached?.some(Boolean)) return cached;
  return EMPTY_BANNER_IMAGES;
}

function ImageStrip({ images, clips, variant }) {
  const smudgeMask =
    variant === "top"
      ? {
          maskImage:
            "linear-gradient(to bottom, #000 0%, #000 72%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to bottom, #000 0%, #000 72%, transparent 100%)",
        }
      : {
          maskImage:
            "linear-gradient(to top, #000 0%, #000 72%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(to top, #000 0%, #000 72%, transparent 100%)",
        };

  return (
    <Box
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        height: "41%",
        bgcolor: "common.white",
        overflow: "hidden",
        ...(variant === "top"
          ? { top: 0, ...smudgeMask }
          : { bottom: 0, ...smudgeMask }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          bgcolor: "common.white",
        }}
      >
        {images.map((src, index) => (
          <Box
            key={`${variant}-${index}`}
            sx={{
              flex: 1,
              minWidth: 0,
              height: "100%",
              ml: index > 0 ? "-1px" : 0,
              clipPath: clips[index],
              overflow: "hidden",
              bgcolor: "common.white",
            }}
          >
            {src ? (
              <Box
                component="img"
                src={src}
                alt={`Catalog product ${index + 1}`}
                loading="eager"
                decoding="async"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                }}
              />
            ) : null}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          height: "34%",
          pointerEvents: "none",
          ...(variant === "top"
            ? {
                bottom: 0,
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.42) 62%, #FFFFFF 100%)",
              }
            : {
                top: 0,
                background:
                  "linear-gradient(0deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.42) 62%, #FFFFFF 100%)",
              }),
        }}
      />
    </Box>
  );
}

const Banner = () => {
  const bannerRef = useRef(null);
  const [bannerImages, setBannerImages] = useState(getInitialBannerImages);
  const [compactStrip, setCompactStrip] = useState(false);

  useEffect(() => {
    const node = bannerRef.current;
    if (!node) return undefined;

    const updateCompact = (width) => {
      setCompactStrip(width < COMPACT_STRIP_WIDTH);
    };

    updateCompact(node.getBoundingClientRect().width);

    const observer = new ResizeObserver(([entry]) => {
      updateCompact(entry.contentRect.width);
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadBannerImages = async () => {
      try {
        const catalogs = await fetchPublicCatalogs();
        if (cancelled) return;

        const resolvedSlots = buildBannerImagesFromCatalogs(catalogs || []);
        const resolvedImages = resolvedSlots.filter(Boolean);

        if (!resolvedImages.length) return;

        await preloadImages(resolvedImages);
        if (cancelled) return;

        setBannerImages(resolvedSlots);
        writeLoginBannerCache(resolvedSlots);
      } catch {}
    };

    loadBannerImages();

    return () => {
      cancelled = true;
    };
  }, []);

  const displayImages = useMemo(() => {
    if (!bannerImages.length) {
      return Array.from({ length: BANNER_IMAGE_COUNT }, () => null);
    }

    const fallbackPool = bannerImages.filter(Boolean);
    if (!fallbackPool.length) {
      return Array.from({ length: BANNER_IMAGE_COUNT }, () => null);
    }

    return Array.from({ length: BANNER_IMAGE_COUNT }, (_, index) => {
      return bannerImages[index] || fallbackPool[index % fallbackPool.length];
    });
  }, [bannerImages]);

  const topStripImages = useMemo(() => {
    const images = displayImages.slice(0, 4);
    return compactStrip ? pickEndcapImages(images) : images;
  }, [compactStrip, displayImages]);

  const bottomStripImages = useMemo(() => {
    const images = displayImages.slice(4, 8);
    return compactStrip ? pickEndcapImages(images) : images;
  }, [compactStrip, displayImages]);

  const topClips = compactStrip ? COMPACT_TOP_CLIPS : TOP_CLIPS;
  const bottomClips = compactStrip ? COMPACT_BOTTOM_CLIPS : BOTTOM_CLIPS;

  return (
    <Box
      ref={bannerRef}
      sx={{
        position: "relative",
        flex: 1,
        width: "100%",
        height: "100vh",
        minHeight: "100vh",
        bgcolor: "common.white",
        overflow: "hidden",
      }}
    >
      <ImageStrip images={topStripImages} clips={topClips} variant="top" />
      <ImageStrip
        images={bottomStripImages}
        clips={bottomClips}
        variant="bottom"
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { md: 1.5, lg: 3.5 },
          py: { md: 4, lg: 5 },
          boxSizing: "border-box",
        }}
      >
        <Stack
          spacing={{ md: 1.4, lg: 1.7 }}
          alignItems="center"
          sx={{
            textAlign: "center",
            width: "100%",
            maxWidth: { md: "100%", lg: 960 },
            mx: "auto",
          }}
        >
          <Typography
            sx={{
              fontSize: "clamp(2rem, 2.1vw + 1.1rem, 4.2rem)",
              lineHeight: 1,
              fontWeight: 800,
              letterSpacing: "-0.025em",
              color: "primary.main",
              width: "100%",
            }}
          >
            Welcome Back.
          </Typography>

          <Typography
            sx={{
              fontSize: "clamp(2rem, 2.1vw + 1.1rem, 4.2rem)",
              lineHeight: 1.25,
              fontWeight: 800,
              letterSpacing: "-0.025em",
              color: "secondary.main",
              mt: "-4px !important",
              width: "max-content",
              maxWidth: "100%",
              mx: "auto",
              whiteSpace: "nowrap",
            }}
          >
            Let&apos;s Build Your Etsy Business.
          </Typography>

          <Typography
            sx={{
              color: "grey.700",
              fontSize: "clamp(0.95rem, 0.35vw + 0.8rem, 1.45rem)",
              lineHeight: 1.42,
              fontWeight: 400,
              maxWidth: 690,
              width: "100%",
            }}
          >
            Access exclusive products, expert trainings, weekly Q&A sessions,
            and proven strategies from top Etsy sellers.
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0}
            sx={{
              mt: { md: 0.5, lg: 0.8 },
              width: "100%",
              flexWrap: "wrap",
              rowGap: 1,
            }}
          >
            {featureItems.map((item, index) => (
              <Stack
                key={item.title}
                direction="row"
                alignItems="center"
                spacing={0}
              >
                <Stack
                  direction="row"
                  spacing={1.15}
                  alignItems="center"
                  sx={{ px: { md: 1.1, lg: 1.55 }, py: 0.5 }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "secondary.main",
                      color: "common.white",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "clamp(0.72rem, 0.25vw + 0.62rem, 0.95rem)",
                      lineHeight: 1.15,
                      color: "primary.main",
                      fontWeight: 700,
                      textAlign: "left",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.title}
                    <br />
                    {item.subtitle}
                  </Typography>
                </Stack>
                {index < featureItems.length - 1 ? (
                  <Box
                    sx={{
                      width: "1px",
                      height: 34,
                      bgcolor: "grey.300",
                      mx: { md: 0.25, lg: 0.5 },
                    }}
                  />
                ) : null}
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: { md: 24, lg: 32 },
          right: { md: 24, lg: 32 },
          zIndex: 3,
        }}
      >
        <NavLink to="/signup" style={{ textDecoration: "none" }}>
          <Button
            size="large"
            variant="contained"
            sx={{
              borderRadius: "999px",
              px: 3,
              py: 1,
              fontWeight: 600,
              fontSize: "0.9375rem",
              textTransform: "none",
              bgcolor: "secondary.main",
              boxShadow: "none",
              transition: "transform 0.2s ease",
              "&:hover": {
                bgcolor: "secondary.dark",
                transform: "scale(1.04)",
                boxShadow: "none",
              },
            }}
          >
            Sign Up
          </Button>
        </NavLink>
      </Box>
    </Box>
  );
};

export default Banner;
